// Plano de mesas: geometría compartida con el POS.
//
// Reglas (deben coincidir exactamente con la app POS):
// - El lienzo de cada sección tiene proporción fija 3:4 vertical (ancho:alto).
// - posX/posY son el CENTRO de la mesa normalizado 0-1 respecto al ancho/alto del lienzo,
//   redondeado a 4 decimales.
// - Los tamaños se definen sobre un lienzo de referencia de 360x480 px y escalan con el ancho real.
// - shape null se dibuja como 'round'.

export type TableShape = 'round' | 'square' | 'rect';

export const SHAPES: TableShape[] = ['round', 'square', 'rect'];

export const REFERENCE_WIDTH = 360;
export const REFERENCE_HEIGHT = 480;
export const CANVAS_ASPECT = REFERENCE_WIDTH / REFERENCE_HEIGHT;   // 3:4

// Tamaño de cada forma en el lienzo de referencia (px).
// Caben 5 mesas por fila en el ancho de 360 (cualquier forma), con espacio entre ellas.
// IMPORTANTE: la app POS debe usar exactamente estos mismos valores.
export const SHAPE_SIZES: Record<TableShape, { width: number; height: number; radius: number | 'circle' }> = {
    round: { width: 56, height: 56, radius: 'circle' },
    square: { width: 54, height: 54, radius: 11 },
    rect: { width: 66, height: 40, radius: 10 },
};

// Cuadrícula de ajuste (en px del lienzo de referencia)
export const GRID_STEP = 24;

export const MIN_SEATS = 1;
export const MAX_SEATS = 50;

// Mesa en el editor
export interface LayoutTable {
    id: number;
    name: string;
    posX: number | null;
    posY: number | null;
    shape: TableShape | null;
    seats: number | null;
}

export interface LayoutPayload {
    sectionId: number | null;
    tables: { id: number; posX: number | null; posY: number | null; shape: TableShape | null; seats: number | null }[];
}

// -------------------------------------------------------------------------
// Conversiones
// -------------------------------------------------------------------------

export const round4 = (value: number) => Math.round(value * 10000) / 10000;

export const effectiveShape = (shape: TableShape | string | null | undefined): TableShape =>
    SHAPES.includes(shape as TableShape) ? shape as TableShape : 'round';

// Alto del lienzo para un ancho dado (3:4)
export const canvasHeightFor = (canvasWidth: number) => canvasWidth / CANVAS_ASPECT;

// Factor de escala respecto al lienzo de referencia
export const scaleFor = (canvasWidth: number) => canvasWidth / REFERENCE_WIDTH;

// Tamaño real de la mesa en un lienzo de cierto ancho
export const tableSize = (shape: TableShape | string | null | undefined, canvasWidth: number) => {
    const size = SHAPE_SIZES[effectiveShape(shape)];
    const scale = scaleFor(canvasWidth);
    return {
        width: size.width * scale,
        height: size.height * scale,
        borderRadius: size.radius === 'circle' ? '50%' : `${size.radius * scale}px`,
    };
};

// Píxeles del lienzo (centro de la mesa) -> coordenadas normalizadas 0-1
export const toNormalized = (centerX: number, centerY: number, canvasWidth: number, canvasHeight: number) => ({
    posX: round4(centerX / canvasWidth),
    posY: round4(centerY / canvasHeight),
});

// Coordenadas normalizadas -> píxeles del centro en el lienzo
export const toPixels = (posX: number, posY: number, canvasWidth: number, canvasHeight: number) => ({
    x: posX * canvasWidth,
    y: posY * canvasHeight,
});

// -------------------------------------------------------------------------
// Límites del arrastre
// -------------------------------------------------------------------------

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Rango permitido del centro (normalizado) para que la mesa completa quede dentro del lienzo.
// Como todo escala con el ancho, el rango es el mismo en cualquier tamaño de pantalla.
export const centerBounds = (shape: TableShape | string | null | undefined) => {
    const size = SHAPE_SIZES[effectiveShape(shape)];
    const halfX = size.width / 2 / REFERENCE_WIDTH;
    const halfY = size.height / 2 / REFERENCE_HEIGHT;
    return { minX: halfX, maxX: 1 - halfX, minY: halfY, maxY: 1 - halfY };
};

export const clampPosition = (posX: number, posY: number, shape: TableShape | string | null | undefined) => {
    const b = centerBounds(shape);
    return { posX: round4(clamp(posX, b.minX, b.maxX)), posY: round4(clamp(posY, b.minY, b.maxY)) };
};

// Ajuste a la cuadrícula (en coordenadas normalizadas), respetando los límites.
// La cuadrícula está anclada al centro del lienzo, así "colocar en el centro" queda exactamente en 0.5, 0.5.
export const snapPosition = (posX: number, posY: number, shape: TableShape | string | null | undefined) => {
    const stepX = GRID_STEP / REFERENCE_WIDTH;
    const stepY = GRID_STEP / REFERENCE_HEIGHT;
    return clampPosition(
        0.5 + Math.round((posX - 0.5) / stepX) * stepX,
        0.5 + Math.round((posY - 0.5) / stepY) * stepY,
        shape
    );
};

// Desplazamiento del fondo de puntos (px) para que coincida con la cuadrícula anclada al centro.
// Cada celda del fondo dibuja su punto en el medio (GRID_STEP / 2).
export const gridBackgroundOffset = (canvasWidth: number) => {
    const scale = scaleFor(canvasWidth);
    const offset = (reference: number) => (((reference / 2) % GRID_STEP) - GRID_STEP / 2) * scale;
    return { x: offset(REFERENCE_WIDTH), y: offset(REFERENCE_HEIGHT) };
};

// -------------------------------------------------------------------------
// Superposición (aviso visual)
// -------------------------------------------------------------------------

const isPlaced = (t: LayoutTable) => t.posX !== null && t.posY !== null;

export const tablesOverlap = (a: LayoutTable, b: LayoutTable): boolean => {
    if (!isPlaced(a) || !isPlaced(b)) return false;
    const sa = SHAPE_SIZES[effectiveShape(a.shape)];
    const sb = SHAPE_SIZES[effectiveShape(b.shape)];
    // En px del lienzo de referencia
    const ax = a.posX! * REFERENCE_WIDTH, ay = a.posY! * REFERENCE_HEIGHT;
    const bx = b.posX! * REFERENCE_WIDTH, by = b.posY! * REFERENCE_HEIGHT;

    if (effectiveShape(a.shape) === 'round' && effectiveShape(b.shape) === 'round') {
        return Math.hypot(ax - bx, ay - by) < (sa.width + sb.width) / 2;
    }
    return Math.abs(ax - bx) < (sa.width + sb.width) / 2 && Math.abs(ay - by) < (sa.height + sb.height) / 2;
};

// Ids de las mesas que se encima con alguna otra
export const overlappingIds = (tables: LayoutTable[]): Set<number> => {
    const ids = new Set<number>();
    const placed = tables.filter(isPlaced);
    for (let i = 0; i < placed.length; i++) {
        for (let j = i + 1; j < placed.length; j++) {
            if (tablesOverlap(placed[i], placed[j])) {
                ids.add(placed[i].id);
                ids.add(placed[j].id);
            }
        }
    }
    return ids;
};

// -------------------------------------------------------------------------
// Operaciones del editor (inmutables)
// -------------------------------------------------------------------------

export const isUnplaced = (t: LayoutTable) => t.posX === null || t.posY === null;

// Coloca (o mueve) una mesa en una posición, dentro de los límites del lienzo
export const placeTable = (tables: LayoutTable[], id: number, posX: number, posY: number, snap = false): LayoutTable[] =>
    tables.map(t => {
        if (t.id !== id) return t;
        const position = snap ? snapPosition(posX, posY, t.shape) : clampPosition(posX, posY, t.shape);
        return { ...t, ...position };
    });

// Coloca una mesa en el centro del lienzo (clic en "Mesas sin ubicar")
export const placeAtCenter = (tables: LayoutTable[], id: number, snap = false) => placeTable(tables, id, 0.5, 0.5, snap);

// Quita la mesa del plano (vuelve a "sin ubicar"); conserva forma y sillas
export const removeFromPlan = (tables: LayoutTable[], id: number): LayoutTable[] =>
    tables.map(t => t.id === id ? { ...t, posX: null, posY: null } : t);

// Cambia la forma y reajusta la posición para que la mesa siga dentro del lienzo
export const setShape = (tables: LayoutTable[], id: number, shape: TableShape): LayoutTable[] =>
    tables.map(t => {
        if (t.id !== id) return t;
        const next = { ...t, shape };
        return isUnplaced(next) ? next : { ...next, ...clampPosition(next.posX!, next.posY!, shape) };
    });

export const setSeats = (tables: LayoutTable[], id: number, seats: number | null): LayoutTable[] =>
    tables.map(t => t.id === id ? { ...t, seats } : t);

export const isValidSeats = (seats: number | null) =>
    seats === null || (Number.isInteger(seats) && seats >= MIN_SEATS && seats <= MAX_SEATS);

// -------------------------------------------------------------------------
// Guardado
// -------------------------------------------------------------------------

// Payload de PUT /api/table/layout: TODA la sección (las mesas sin ubicar van con posX/posY null)
export const buildLayoutPayload = (sectionId: number | null, tables: LayoutTable[]): LayoutPayload => ({
    sectionId,
    tables: tables.map(t => {
        const placed = !isUnplaced(t);
        return {
            id: t.id,
            posX: placed ? round4(t.posX!) : null,
            posY: placed ? round4(t.posY!) : null,
            shape: t.shape ?? null,
            seats: t.seats ?? null,
        };
    }),
});

// ¿Hay diferencias con lo guardado?
export const layoutChanged = (current: LayoutTable[], saved: LayoutTable[]) =>
    JSON.stringify(buildLayoutPayload(null, current).tables) !== JSON.stringify(buildLayoutPayload(null, saved).tables);
